import React, { useEffect, useState, useCallback } from 'react';
import { backupAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  Database, Download, Play, Trash2, RefreshCw, ShieldAlert, Clock,
  HardDrive, CheckCircle, AlertCircle, X, Loader2, Undo2, Cloud, Calendar,
  Crown,
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────
const formatBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 ? 2 : 1)} ${units[i]}`;
};

const formatDate = (iso, opts = {}) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: opts.year ? 'numeric' : undefined,
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const TYPE_STYLE = {
  daily:   { color: '#166534', label: 'Daily' },
  weekly:  { color: '#D4AF37', label: 'Weekly' },
  monthly: { color: '#0F0F0F', label: 'Monthly' },
  manual:  { color: '#B45309', label: 'Manual' },
};

const STATUS_STYLE = {
  success:     { color: '#166534', label: 'Success' },
  in_progress: { color: '#D4AF37', label: 'In Progress' },
  failed:      { color: '#991B1B', label: 'Failed' },
  pruned:      { color: '#595959', label: 'Pruned' },
  deleted:     { color: '#595959', label: 'Deleted' },
};

const AdminBackup = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null); // {id, filename}
  const [restoreInput, setRestoreInput] = useState('');
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [err, setErr] = useState('');

  const isFounder = user?.role === 'founder';

  const load = useCallback(async () => {
    if (!isFounder) { setLoading(false); return; }
    setLoading(true);
    try {
      const [s, h] = await Promise.all([backupAPI.summary(), backupAPI.list()]);
      setSummary(s); setHistory(h); setErr('');
    } catch (e) {
      setErr(e.message || 'Failed to load Backup Center');
    } finally { setLoading(false); }
  }, [isFounder]);

  useEffect(() => { load(); }, [load]);

  const runBackup = async () => {
    setRunning(true); setErr('');
    try {
      await backupAPI.run();
      await load();
    } catch (e) {
      setErr(e.message || 'Backup failed');
    } finally { setRunning(false); }
  };

  const removeBackup = async (id) => {
    if (!window.confirm('Permanently delete this backup?')) return;
    try { await backupAPI.remove(id); load(); }
    catch (e) { alert(e.message || 'Delete failed'); }
  };

  const confirmRestore = async () => {
    if (restoreInput !== 'RESTORE' || !restoreTarget) return;
    setRestoreBusy(true);
    try {
      const r = await backupAPI.restore(restoreTarget.id);
      alert(`Restore complete.\nRestored from: ${r.restored_from}\nSafety snapshot: ${r.safety_backup}`);
      setRestoreTarget(null); setRestoreInput('');
      load();
    } catch (e) {
      alert(e.message || 'Restore failed');
    } finally { setRestoreBusy(false); }
  };

  // ─── Founder-only guard ─────────────────────────
  if (!isFounder) {
    return (
      <div data-testid="backup-locked" className="text-center py-14">
        <Crown className="w-10 h-10 mx-auto mb-3" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
        <p className="text-[13px]" style={{ color: '#0F0F0F', fontFamily: 'Playfair Display, serif' }}>
          Backup Center is Founder-only
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#595959' }}>
          Only the founder account can view, run, or restore backups.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div data-testid="admin-backup" className="space-y-3">
      {err && (
        <div className="border-l-4 p-3 flex items-start gap-2"
          style={{ borderColor: '#991B1B', backgroundColor: 'rgba(153,27,27,0.06)' }}>
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#991B1B' }} strokeWidth={1.5} />
          <p className="text-[11px]" style={{ color: '#991B1B' }}>{err}</p>
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase" style={{ color: '#D4AF37' }}>Founder Only</p>
          <h2 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Backup Center</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="backup-refresh" onClick={load} className="p-2">
            <RefreshCw className="w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
          </button>
          <button
            data-testid="backup-run-now"
            onClick={runBackup}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.1em] uppercase font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: '#0F0F0F' }}>
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> : <Play className="w-3.5 h-3.5" strokeWidth={2} />}
            {running ? 'Running…' : 'Run Backup Now'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div data-testid="backup-stat-status" className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
              <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>Restore Readiness</p>
              <div className="mt-1 flex items-center gap-1.5">
                {summary.restore_ready ? <CheckCircle className="w-5 h-5" style={{ color: '#166534' }} strokeWidth={1.5} /> : <AlertCircle className="w-5 h-5" style={{ color: '#991B1B' }} strokeWidth={1.5} />}
                <p className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: summary.restore_ready ? '#166534' : '#991B1B' }}>
                  {summary.restore_ready ? 'Ready' : 'Stale'}
                </p>
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: '#595959' }}>
                Backup age &lt; 48 h
              </p>
            </div>

            <div data-testid="backup-stat-count" className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
              <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>Active Backups</p>
              <p className="text-2xl mt-1" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                {summary.total_backups_active}
              </p>
              <p className="text-[10px]" style={{ color: '#595959' }}>
                {summary.total_backups_lifetime} lifetime
              </p>
            </div>

            <div data-testid="backup-stat-size" className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
              <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>Total Size</p>
              <p className="text-2xl mt-1" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                {formatBytes(summary.total_size_bytes)}
              </p>
              <div className="flex items-center gap-1 mt-0.5 text-[10px]" style={{ color: '#595959' }}>
                {summary.storage_backend === 's3' ? <Cloud className="w-3 h-3" strokeWidth={1.5} /> : <HardDrive className="w-3 h-3" strokeWidth={1.5} />}
                <span className="uppercase tracking-[0.05em]">{summary.storage_backend}</span>
              </div>
            </div>

            <div data-testid="backup-stat-last" className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
              <p className="text-[9px] tracking-[0.12em] uppercase" style={{ color: '#595959' }}>Last Success</p>
              <p className="text-[13px] mt-1" style={{ color: '#0F0F0F' }}>
                {summary.last_success ? formatDate(summary.last_success.completed_at) : '—'}
              </p>
              {summary.last_success && (
                <p className="text-[10px]" style={{ color: '#595959' }}>{formatBytes(summary.last_success.size_bytes)}</p>
              )}
            </div>
          </div>

          {/* Next scheduled */}
          <div data-testid="backup-next-scheduled" className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
            <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#0F0F0F' }}>
              <Calendar className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Next Scheduled Runs (UTC)
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {['daily', 'weekly', 'monthly'].map(k => {
                const s = TYPE_STYLE[k];
                return (
                  <div key={k} className="border p-2" style={{ borderColor: '#E5E5E5' }}>
                    <p className="text-[9px] tracking-[0.1em] uppercase" style={{ color: s.color }}>{s.label}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: '#0F0F0F' }}>
                      {formatDate(summary.next_scheduled?.[k])}
                    </p>
                  </div>
                );
              })}
            </div>
            <p className="text-[9px] mt-2" style={{ color: '#595959' }}>
              Retention: Daily 30d · Weekly 12w · Monthly 12m
            </p>
          </div>

          {/* Last failure alert */}
          {summary.last_failure && (
            <div className="border-l-4 p-3" style={{ borderColor: '#991B1B', backgroundColor: 'rgba(153,27,27,0.06)' }}>
              <p className="text-[10px] tracking-[0.12em] uppercase" style={{ color: '#991B1B' }}>
                <ShieldAlert className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Last Failure
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#0F0F0F' }}>
                {summary.last_failure.error_message || 'Unknown reason'}
              </p>
              <p className="text-[10px] mt-0.5" style={{ color: '#595959' }}>{formatDate(summary.last_failure.completed_at)}</p>
            </div>
          )}
        </>
      )}

      {/* History */}
      <div>
        <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: '#0F0F0F' }}>
          <Database className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Backup History
        </p>
        {history.length === 0 && (
          <p className="text-center text-[11px] py-6" style={{ color: '#595959' }}>No backups yet — press "Run Backup Now" to create your first.</p>
        )}
        <div className="space-y-1.5">
          {history.map(row => {
            const typ = TYPE_STYLE[row.type] || TYPE_STYLE.manual;
            const st = STATUS_STYLE[row.status] || STATUS_STYLE.success;
            const canDownload = row.status === 'success';
            const canRestore = row.status === 'success';
            return (
              <div key={row.id} data-testid={`backup-row-${row.id}`} className="bg-white border p-3" style={{ borderColor: '#E5E5E5', borderLeft: `3px solid ${typ.color}` }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="px-1.5 py-px text-[9px] tracking-[0.08em] uppercase font-medium" style={{ backgroundColor: `${typ.color}18`, color: typ.color }}>{typ.label}</span>
                      <span className="px-1.5 py-px text-[9px] tracking-[0.08em] uppercase" style={{ backgroundColor: `${st.color}18`, color: st.color }}>{st.label}</span>
                      {row.storage && <span className="px-1.5 py-px text-[9px] tracking-[0.05em] uppercase" style={{ backgroundColor: '#F0F0F0', color: '#595959' }}>{row.storage}</span>}
                    </div>
                    <p className="text-[11px] font-mono truncate" style={{ color: '#0F0F0F' }}>{row.filename}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px]" style={{ color: '#595959' }}>
                      <Clock className="w-3 h-3" strokeWidth={1.5} />
                      <span>{formatDate(row.started_at, { year: true })}</span>
                      <span>·</span>
                      <span>{formatBytes(row.size_bytes || 0)}</span>
                      {row.duration_ms > 0 && <><span>·</span><span>{(row.duration_ms / 1000).toFixed(1)}s</span></>}
                    </div>
                    {row.error_message && (
                      <p className="text-[10px] mt-1" style={{ color: '#991B1B' }}>⚠ {row.error_message}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {canDownload && (
                      <a data-testid={`backup-download-${row.id}`}
                        href={backupAPI.downloadUrl(row.id)}
                        className="p-2"
                        title="Download">
                        <Download className="w-4 h-4" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
                      </a>
                    )}
                    {canRestore && (
                      <button data-testid={`backup-restore-${row.id}`}
                        onClick={() => { setRestoreTarget({ id: row.id, filename: row.filename }); setRestoreInput(''); }}
                        className="p-2" title="Restore">
                        <Undo2 className="w-4 h-4" style={{ color: '#B45309' }} strokeWidth={1.5} />
                      </button>
                    )}
                    <button data-testid={`backup-delete-${row.id}`}
                      onClick={() => removeBackup(row.id)}
                      className="p-2" title="Delete">
                      <Trash2 className="w-4 h-4" style={{ color: '#991B1B' }} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Restore confirmation modal */}
      {restoreTarget && (
        <div data-testid="restore-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(15,15,15,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white shadow-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E5E5E5' }}>
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" style={{ color: '#991B1B' }} strokeWidth={2} />
                <h3 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                  Restore Database
                </h3>
              </div>
              <button data-testid="restore-cancel-x"
                onClick={() => { setRestoreTarget(null); setRestoreInput(''); }}
                className="p-1">
                <X className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="p-3 border-l-4" style={{ borderColor: '#991B1B', backgroundColor: 'rgba(153,27,27,0.06)' }}>
                <p className="text-[11px] font-medium mb-1" style={{ color: '#991B1B' }}>Warning — irreversible action</p>
                <p className="text-[11px]" style={{ color: '#0F0F0F' }}>
                  This will overwrite every collection in the database with the contents of
                  the selected backup. Any data created since that backup will be lost.
                </p>
                <p className="text-[10px] mt-2" style={{ color: '#595959' }}>
                  A safety snapshot of the current DB will be taken first, so you can roll
                  back if needed. The restore event is logged in the Error Center.
                </p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.1em] uppercase mb-1" style={{ color: '#595959' }}>Restoring from</p>
                <p className="text-[12px] font-mono" style={{ color: '#0F0F0F' }}>{restoreTarget.filename}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.1em] uppercase mb-1" style={{ color: '#595959' }}>
                  Type <span style={{ color: '#0F0F0F' }}>RESTORE</span> to confirm
                </p>
                <input
                  data-testid="restore-confirm-input"
                  value={restoreInput}
                  onChange={e => setRestoreInput(e.target.value)}
                  placeholder="RESTORE"
                  className="w-full px-3 py-2 text-[13px] font-mono border focus:outline-none focus:border-black"
                  style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }}
                />
              </div>
            </div>
            <div className="flex gap-2 px-5 py-3 border-t" style={{ borderColor: '#E5E5E5' }}>
              <button data-testid="restore-cancel"
                onClick={() => { setRestoreTarget(null); setRestoreInput(''); }}
                className="flex-1 py-2 text-[11px] tracking-[0.1em] uppercase border"
                style={{ borderColor: '#E5E5E5', color: '#595959' }}>
                Cancel
              </button>
              <button data-testid="restore-confirm"
                onClick={confirmRestore}
                disabled={restoreInput !== 'RESTORE' || restoreBusy}
                className="flex-1 py-2 text-[11px] tracking-[0.1em] uppercase font-medium text-white disabled:opacity-40"
                style={{ backgroundColor: '#991B1B' }}>
                {restoreBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" strokeWidth={2} /> Restoring…</> : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackup;
